namespace Core.ManageBooking {

    export interface ItineraryBase {
        ItineraryId: number;
        StartDate?: string;
        EndDate?: string;
        Description?: string;
        DestinationPoint?: string;
    }

    export type ItineraryItem =
        FlightItinerary |
        AccommodationItinerary |
        TransferItinerary |
        MeetAndGreetItinerary |
        AdHocItinerary;

    export interface FlightItinerary extends ItineraryBase {
        Type: ItineraryType;
        CarrierName?: string;
        DepartureAirportCode?: string;
        DestinationAirportCode?: string;
        FlightNumber?: string;
        BookingClass: string;
    }

    export interface AccommodationItinerary extends ItineraryBase {
        Type: ItineraryType;
        Occuppancy?: Occuppancy;
        EstabTitle?: string;
        Province?: string;
        Address?: string;
        Phone?: string;
        RoomType?: string;
        BoardType?: string;
        EstabId: number;
    }

    export interface AdHocItinerary extends ItineraryBase {
        Type: ItineraryType;
        Adults: number;
        Children: number;
        Infants: number;
    }

    export interface MeetAndGreetItinerary extends ItineraryBase {
        Type: ItineraryType;
        /** Meet and Greet location, airport code */
        DeparturePoint?: string;
        /** The flight number stored against the Meet and Greet itinerary item
        (may be comma separated list of flight numbers) */
        FlightNo?: string;
        /** If we are able to identify the connecting flight,
        this is the flight booking id (may be null) */
        ConnectingFlightBookingId: number;
        /** If we are able to identify the connecting flight,
        this is the flight itinerary id (may be null) */
        ConnectingFlightItineraryId: number;
        /** If we are able to identify the connecting flight,
        this is the flight number recorded against the flight itinerary (may be null) */
        ConnectingFlightNo?: string;
        /** If we are able to identify the connecting flight,
        this is the flight description (contains "via" information if there is more than one flight number) */
        ConnectingFlightDescription?: string;
    }

    export interface TransferItinerary extends ItineraryBase {
        Type: ItineraryType;
        DeparturePoint?: string;
        FlightNo?: string;
    }

    export interface BundleItinerary extends ItineraryBase {
        TypeId: number;
    }

    export interface BookedItem {
        Description?: string;
        UnitAmount: number;
        Quantity: number;
        AtolProtected: boolean;
        ClientCurrencyCode?: string;
        UnitSupplierNet: number;
        UnitMargin: number;
        UnitTravelAgentCommission: number;
        BookedItemId: number;
        ReversedBookedItemId: number;
        ItineraryId: number;
        // Front-end props
        Selected: boolean;
    }

    export interface Booking {
        Summary?: BookingSummary2;
        Contact?: BookingContact;
        TravelAgent?: TravelAgent;
    }

    export interface LeadPassenger {
        FirstName?: string;
        LastName?: string;
        Title?: string;
        Email?: string;
        PassengerFrom?: string;
        CountryCode?: string;
    }

    export interface Passenger {
        TypeId: number;
        AgeOnReturn: number;
        Title?: string;
        LastName?: string;
        FirstName?: string;
    }

    export interface Occuppancy {
        Adults: number;
        Children: number;
        Infants: number;
    }

    export interface TravelAgent {
        Id: number;
        Title?: string;
        FirstName?: string;
        LastName?: string;
    }

    export enum BookingStatus {
        Pending = 1,
        Receipted = 2,
        Ticketed = 4,
        Complete = 5,
        CancelledByClient = 6,
        Void = 7,
        HoldAwaitingConfirmation = 8,
        DeclinedByEstab = 9,
        ConfirmedByEstab = 10,
        CancellationConfirmedByEstab = 11,
        CancellationDeclinedByEstab = 12,
        FailedSecurityCheck = 13,
        HoldSupplierSwap = 14,
        AutoAccepted = 15,
        PriceUp = 16,
        NotAvailable = 17,
        AwaitingCustomerResponse = 18,
        Migrated = 19,
    }

    export enum BookingInfoItemType {
        Invalid = 0,
        Generic = 1,
        CancellationCondition = 2,
        PublishedNote = 3,
        BaggageInformation = 4,
        TerminalInformation = 5,
        FareRules = 6,
        OutboundFlights = 7,
        InboundFlights = 8,
        SpecialOffer = 9,
        BookingCondition = 10,
    }

    export interface BookingSummary {
        BookingId: number;
        Title?: string;
        BookingRef?: string;
        Type: BookingType;
        StartDate: string;
        ReturnDate: string;
        Duration: number;
    }

    export enum ItineraryType {
        FlightOutbound = 1,
        FlightInbound = 2,
        CarHire = 3,
        HotelRoom = 4,
        AttractionTicket = 5,
        FlightInfo = 6,
        CarParking = 7,
        TransferArrival = 8,
        TransferReturn = 9,
        Insurance = 10,
        PackageHoliday = 11,
        Marhaba = 13,
        Bundle = 14,
        PointToPointTransfer = 15,
        AdHoc = 16,
    }

    export enum BookingType {
        Flight = 1,
        AttractionTickets = 2,
        CarHire = 4,
        Accommodation = 8,
        TailorMadeHoliday = 9,
        Parking = 16,
        Transfer = 32,
        TravelInsurance = 64,
        PackageHolidays = 128,
        Marhaba = 256,
        CruiseHolidays = 512,
        Bundle = 1024,
        AdHoc = 2048,
    }

    export interface BookingSummary2 {
        BookingId: number;
        BookingRef?: string;
        SiteId?: string;
        Type: BookingType;
        StartDate: string;
        SupplierId: number;
        DomainBookedOn?: Domain;
        DomainBookedOnId: number;
    }

    export interface BookingContact {
        Title?: string;
        FirstName?: string;
        LastName?: string;
        Email?: string;
        LoyaltyNumber?: string;
        SiteId?: string;
        BookingId: number;
    }

    export interface Domain {
        Id: number;
        Name?: string;
        Label?: string;
    }

    export interface BookingAndLeadPassengerResponse {
        Booking?: BookingSummary;
        LeadPassenger?: LeadPassenger;
    }

    export interface BookingDetail extends BookingAndLeadPassengerResponse {
        BookedItems?: BookedItem[];
        Itineraries?: ItineraryBase[];
        BookingStatus: BookingStatus;
        BookingInfoItems?: BookingInfoItem[];
    }

    export interface BookingInfoItem {
        Id: number;
        BookingId: number;
        Title?: string;
        Description?: string;
        ShowOnVoucher: boolean;
        BookingInfoItemType: BookingInfoItemType;
    }

}
